import { SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Card,
  ExternalLink,
  InlineAlert,
  LgPadding,
  Link,
  MdSpacing,
  Tag,
  useScreenSize,
} from '@alto/design-system';
import { CalendarRow, CreditCardRow, MedRow } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import {
  type PaymentMethod,
  type ShipmentInfo,
} from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/update_payment_method_patient_task';
import React from 'react';
import { Link as RRLink } from 'react-router-dom';
import styledNative from 'styled-components/native';
import { initializeExistingOrder, setOrderAtIndex, updateOrder } from '~shared/actions/checkout';
import { getCompanyName } from '~shared/config/getCompanyName';
import { getCompanyPhone } from '~shared/config/getCompanyPhone';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { getExistingOrder } from '~shared/features/checkout/selectors/getOrder';
import { SECTION_LABELS } from '~shared/features/homescreen/constants';
import { formatWindowTime } from '~shared/helpers/date';
import { formatCreditCards } from '~shared/helpers/payment';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import illustration from '~web/images/creditCard.png';

const CustomCard = styledNative(Card)<{ isSMScreenOrBigger: boolean; children: React.ReactNode }>`
  margin: 0 0 ${SPACING.STATIC.XL.px};
  ${({ isSMScreenOrBigger }) => isSMScreenOrBigger && `margin: 0 0 ${SPACING.STATIC.XXL.px};`}
  z-index: 0;
`;

type Props = {
  readonly paymentMethods: PaymentMethod[];
  readonly shipment: ShipmentInfo;
  readonly onViewDelivery: (id: number) => void;
};

const tag = (
  <Tag
    label="Action Required"
    type="warning"
  />
);

export default function UpdatePaymentMethod({ shipment, onViewDelivery, paymentMethods }: Props) {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { date, start_time, end_time, cutoff_time } = shipment;
  const splitPayments = shipment.split_payment;
  const { isSMScreenOrBigger } = useScreenSize();
  const order = useSelectorShared((state) => getExistingOrder(state, { shipmentID: shipment.id }));
  const phoneNumber = getCompanyPhone();

  const handleUpdatePaymentMethod = () => {
    dispatch(setOrderAtIndex({ order, orderIndex: 0 }));
    dispatch(
      initializeExistingOrder({
        order,
        shipmentID: shipment.id,
      }),
    );
    dispatch(
      updateOrder({
        origin: 'Existing Order',
      }),
    );
    navigate('RouteCheckout');
  };

  return (
    <CustomCard
      tag={tag}
      description={`Update by ${cutoff_time ? formatWindowTime({ windowTime: cutoff_time, meridiem: 'aaa' }) : null}, for scheduled delivery`}
      title={SECTION_LABELS.UPDATE_PAYMENT_METHOD}
      illustrationSrc={illustration}
      illustrationAccessibilityLabel={IMG_ALT_TEXTS.creditCard}
      isSMScreenOrBigger={isSMScreenOrBigger}
    >
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <CalendarRow
          date={date}
          startTime={start_time}
          endTime={end_time}
        />
        <MedRow medicationNames={shipment.medication_names} />
        <CreditCardRow copy={formatCreditCards(paymentMethods)} />
        <MdSpacing />
        {splitPayments ? (
          <InlineAlert type="warning">
            <Body fontFamily="semibold">You are using two payment methods</Body>
            <Body>
              Update your payment information using the ${getCompanyName()} mobile app, or you can{' '}
              <RRLink to="/messages">
                <Link>message us</Link>
              </RRLink>{' '}
              or call us at <ExternalLink href={`tel:${phoneNumber}`}>{phoneNumber}</ExternalLink>.
            </Body>
          </InlineAlert>
        ) : null}
        {!splitPayments && (
          <Button
            type="primary"
            onPress={handleUpdatePaymentMethod}
          >
            Update now
          </Button>
        )}
        <MdSpacing />
        <Button
          type={splitPayments ? 'secondary' : 'tertiary'}
          onPress={() => {
            onViewDelivery(shipment.id);
          }}
        >
          View delivery
        </Button>
      </LgPadding>
    </CustomCard>
  );
}
